import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  isDevMode,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ITransactionDefinition } from '@app/transaction-builder/model/transaction-definition.model';
import { fileToBase64, fileToString } from '@app/transaction-builder/utils/file.util';
import { IDocumentData } from '@app/transaction-builder/model/document-data.model';
import { TransactionFormService } from '@app/transaction-builder/services/transaction-form.service';
import { TransformTransactionDataService } from '@app/transaction-builder/services/transform-transaction-data.service';
import { TransactionBuilderStorageService } from '@app/transaction-builder/services/transaction-builder-storage.service';
import { ITransactionFormData } from '@app/transaction-builder/model/transaction-form-data.model';
import { ITransactionData } from '@app/transaction-builder/model/transaction-data.model';
import { SaveTransactionAction } from '@app/transaction-builder/model/save-transaction-actions.model';
import { ApiService } from '@app/transaction-builder/services/api.service';
import { forkJoin, Observable } from 'rxjs';
import { SubSink } from 'subsink';
import { debounceTime, mergeMap, take, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import {
  IDocumentSwapEvent,
  IEditSignFieldsEvent,
  IRemoveDocumentEvent,
} from '@app/transaction-builder/model/document-events.model';
import { SignersService } from '@app/transaction-builder/services/signers.service';
import {
  ClientSignFieldsByDocument,
  IClientSignField,
} from '@app/transaction-builder/model/sign-field.model';
import { v4 as uuidv4 } from 'uuid';
import { MatDialog } from '@angular/material';
import { DocumentEditorPage } from '@app/transaction-builder/pages/document-editor/document-editor.page';
import { DialogService, EnvConfigService, SignatureType } from 'ngx-mat-esign360';
import { IStartSigningProfile, IStartSigningEnvConfig } from '@app/core/model/env-config.model';
import { ProfileService } from '@app/core/services/profile.service';
import { TranslateService } from '@ngx-translate/core';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ThemedImageName } from '../../../../../../ngx-mat-esign360/src/lib/themed-image/model/themed-image.model';
import { EmptyTransactionDataFactoryService } from '@app/transaction-builder/services/empty-transaction-data-factory.service';
import { JsonEditorComponent, JsonEditorOptions } from 'ang-jsoneditor';

@Component({
  templateUrl: './transaction-builder.page.html',
  styleUrls: ['./transaction-builder.page.scss'],
})
export class TransactionBuilderPage implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('importRef') importRef: ElementRef;
  @ViewChild(JsonEditorComponent) jsonEditorRef: JsonEditorComponent;

  transactionData: ITransactionData;
  formGroup: FormGroup;
  webSignerLink: string = null;
  profile: IStartSigningProfile;
  jsonEditorFormControl = new FormControl('');
  jsonEditorOptions = new JsonEditorOptions();
  configJsonEditorOptions = new JsonEditorOptions();
  private subs = new SubSink();

  get clientsFormArray(): FormArray {
    return this.formGroup.get('clients') as FormArray;
  }

  get clientsToDocsFormArray(): FormArray {
    return this.formGroup.get('documents').get('clientsToDocs') as FormArray;
  }

  constructor(
    private transactionFormService: TransactionFormService,
    private api: ApiService,
    private transformTransactionDataService: TransformTransactionDataService,
    private transactionBuilderStorage: TransactionBuilderStorageService,
    private signersService: SignersService,
    private dialogService: DialogService,
    public dialog: MatDialog,
    public envConfig: EnvConfigService<IStartSigningEnvConfig>,
    private profileService: ProfileService,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private emptyTransactionDataFactoryService: EmptyTransactionDataFactoryService,
    private changeDetector: ChangeDetectorRef
  ) {
    this.jsonEditorOptions.mode = 'code';

    this.configJsonEditorOptions.mode = 'view';
    this.configJsonEditorOptions.expandAll = true;
  }

  ngOnInit() {
    this.subs.sink = this.route.queryParams.subscribe(() => {
      this.initProfile();
    });
  }

  ngAfterViewInit(): void {
    this.changeDetector.detectChanges();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  updateTransactionData(tranasctionData: ITransactionData): void {
    if (this.profile.rememberLastData) {
      this.transactionBuilderStorage.setTransactionData(tranasctionData);
    }

    this.transactionData = tranasctionData;
  }

  initData(tranasctionData: ITransactionData) {
    this.updateTransactionData(tranasctionData);
    this.initFormGroup(tranasctionData.formData);
    this.applyClientIdAutoIncrement();
  }

  initFormGroup(formData?: ITransactionFormData) {
    this.formGroup = this.transactionFormService.createFormGroup(
      (clientCid: string) => this.getSignFieldsByClientCid(clientCid),
      formData
    );
    this.initFormGroupEvents();

    if (formData) {
      this.formGroup.setValue(formData);
    }
  }

  initFormGroupEvents() {
    this.subs.sink = this.formGroup.valueChanges.pipe(debounceTime(400)).subscribe((value) => {
      this.saveTransactionFormData(this.formGroup.getRawValue());
    });

    this.subs.sink = this.clientsToDocsFormArray.valueChanges.subscribe((value) => {
      // wait for the whole formGroup value to update
      setTimeout(() => {
        this.onClientsToDocsChange();
      });
    });
  }

  initProfile(): void {
    const profileId = this.profileService.getCurrentProfileId();

    this.profile = this.profileService.getCurrentProfile();

    if (localStorage.getItem('lastCustomProfile') !== profileId || !this.profile.rememberLastData) {
      this.resetForm();
    } else {
      const transactionData: ITransactionData = this.transactionBuilderStorage.getTransactionData();
      this.initData(transactionData);
    }
    localStorage.setItem('lastCustomProfile', profileId);
  }

  onClientsToDocsChange() {
    const signFieldsByDocument = this.transactionData.signFieldsByDocument;

    Object.keys(signFieldsByDocument).forEach((documentName) => {
      const signFields = signFieldsByDocument[documentName];
      const signers = this.signersService.extractDocSignersFromTxData(
        this.transactionData,
        documentName
      );
      const newSignFields = signFields.filter((signField) =>
        signers.some((signer) => signer.cid === signField.client.cid)
      );

      this.saveTransactionDocSignFields(documentName, newSignFields);
    });
  }

  saveTransactionFormData(formData: ITransactionFormData) {
    this.updateTransactionData({
      ...this.transactionData,
      formData: this.formGroup.getRawValue(),
    });
  }

  saveTransactionDocSignFields(documentName: string, signFields: IClientSignField[]) {
    this.updateTransactionData({
      ...this.transactionData,
      signFieldsByDocument: {
        ...this.transactionData.signFieldsByDocument,
        [documentName]: signFields,
      },
    });
  }

  onAddClient() {
    this.clientsFormArray.push(
      this.transactionFormService.createClientFormGroup((clientCid: string) =>
        this.getSignFieldsByClientCid(clientCid)
      )
    );

    this.clientsToDocsFormArray.controls.forEach((docRelationsFormArray: FormArray) => {
      docRelationsFormArray.push(this.transactionFormService.createDocRelationFormControl());
    });

    this.initData({
      ...this.transactionData,
      formData: this.formGroup.getRawValue(),
    });
  }

  onDocumentsSelected(documents: File[] = null) {
    if (documents && !!documents.length) {
      this.subs.sink = forkJoin(documents.map((document) => fileToBase64(document))).subscribe(
        (docContents) => this.onSelectedDocumentsLoaded(documents, docContents)
      );
    }
  }

  onSelectedDocumentsLoaded(documents: File[] = null, docContents: string[]) {
    const newDocumentsData = documents.map((document, index) => ({
      cid: uuidv4(),
      name: document.name,
      content: docContents[index],
    }));

    this.addDocuments(newDocumentsData);
  }

  addDocuments(newDocumentsData: IDocumentData[]) {
    const newSignFieldsByDocument = { ...this.transactionData.signFieldsByDocument };

    newDocumentsData.forEach((documentData) => {
      newSignFieldsByDocument[documentData.cid] = newSignFieldsByDocument[documentData.cid] || [];
    });

    this.initData({
      ...this.transactionData,
      formData: {
        ...this.transactionData.formData,
        documents: {
          clientsToDocs: this.transactionData.formData.documents.clientsToDocs.concat(
            newDocumentsData.map((document) =>
              this.transactionData.formData.clients.map(() => null)
            )
          ),
        },
      },
      documentsData: this.transactionData.documentsData.concat(newDocumentsData),
      signFieldsByDocument: newSignFieldsByDocument,
    });
  }

  onRemoveClientCard(index: number) {
    const clientCid = this.clientsFormArray.at(index).get('cid').value;

    this.clientsFormArray.removeAt(index);
    this.clientsToDocsFormArray.controls.forEach((docRelationsFormArray: FormArray) => {
      docRelationsFormArray.removeAt(index);
    });

    const newSignFieldsByDoc = Object.entries(
      this.transactionData.signFieldsByDocument
    ).reduce<ClientSignFieldsByDocument>((filteredSignFieldsByDoc, docSignFieldsEntry) => {
      const documentId = docSignFieldsEntry[0];
      const docSignFields = docSignFieldsEntry[1];

      filteredSignFieldsByDoc[documentId] = docSignFields.filter(
        (signField) => signField.client.cid !== clientCid
      );
      return filteredSignFieldsByDoc;
    }, {});

    this.initData({
      ...this.transactionData,
      formData: this.formGroup.getRawValue(),
      signFieldsByDocument: newSignFieldsByDoc,
    });
  }

  transactionDataToTransactionDefinition(startTransaction: boolean): ITransactionDefinition {
    return this.transformTransactionDataService.transactionDataToTransactionDefinition(
      this.transactionData,
      startTransaction
    );
  }

  openTransactionSaveErrorDialog(): Observable<boolean> {
    return this.dialogService.openAlertDialog({
      message: this.translate.instant(
        marker('TRANSACTION_BUILDER.TRANSACTION_SAVED_DIALOG.TRANSACTION_UNSUCCESS_SAVE_AND_SENT')
      ),
      themedImage: ThemedImageName.Error,
    });
  }

  openTransactionSaveSuccessDialog(
    startTransaction: boolean,
    transactionId: string
  ): Observable<boolean> {
    if (startTransaction) {
      return this.dialogService
        .openAlertDialog({
          message: this.translate.instant(
            marker('TRANSACTION_BUILDER.TRANSACTION_SAVED_DIALOG.TRANSACTION_SUCCESS_SAVE_AND_SENT')
          ),
          themedImage: ThemedImageName.Send,
        })
        .pipe(
          tap(() => window.open(`${this.envConfig.data.agentUrl}/transactions/${transactionId}`))
        );
    } else {
      return this.dialogService.openAlertDialog({
        message: this.translate.instant(
          marker('TRANSACTION_BUILDER.TRANSACTION_SAVED_DIALOG.TRANSACTION_SUCCESS_SAVE')
        ),
        themedImage: ThemedImageName.Save,
      });
    }
  }

  onSaveTransaction(startTransaction = false) {
    this.formGroup.markAllAsTouched();
    this.subs.sink = this.api
      .createTransaction(this.transactionDataToTransactionDefinition(startTransaction))
      .pipe(
        mergeMap((transactionId) => {
          return this.openTransactionSaveSuccessDialog(startTransaction, transactionId);
        }),
        take(1)
      )
      .subscribe(
        () => {
          this.resetForm();
        },
        (error: unknown) => {
          console.error(error);
          this.openTransactionSaveErrorDialog();
        }
      );
  }

  onSaveAction(actionId: SaveTransactionAction) {
    switch (actionId) {
      case SaveTransactionAction.Save:
        this.onSaveTransaction(false);
        break;
      case SaveTransactionAction.Export:
        this.onExportFormData();
        break;
    }
  }

  onCaptureFormData() {
    try {
      this.transactionBuilderStorage.setCapturedTransactionData(this.transactionData);
    } catch (error: unknown) {
      // data are too big for local storage
      this.dialogService.openAlertDialog({
        message: this.translate.instant(marker('TRANSACTION_BUILDER.DATA.REMEMBER_ERROR')),
        themedImage: ThemedImageName.Error,
      });
    }
  }

  onPrepopulateForm() {
    const transactionData = this.transactionBuilderStorage.getCapturedTransactionData();

    if (transactionData) {
      this.initData(transactionData);
    } else {
      console.error('No captured data found in local storage.');
    }
  }

  onExportFormData() {
    const transactionData = JSON.stringify(this.transactionData);
    const uri = `data:text/json;charset=UTF-8,${encodeURIComponent(transactionData)}`;
    const linkRef = document.createElement('a');
    const event = new MouseEvent('click');

    linkRef.setAttribute('href', uri);
    linkRef.setAttribute('download', 'transactionData.json');
    linkRef.dispatchEvent(event);
    linkRef.remove();
  }

  onDataImport(files: FileList) {
    this.subs.sink = fileToString(files.item(0))
      .pipe(take(1))
      .subscribe((fileData) => {
        const transactionData = JSON.parse(fileData) as ITransactionData;
        this.initData(transactionData);
      });
  }

  onEditSignFields(event: IEditSignFieldsEvent) {
    const dialogRef = this.dialog.open(DocumentEditorPage, {
      height: '90vh',
      panelClass: 'app-sign-field-editor-dialog',
      autoFocus: false,
      data: {
        documentData: event.documentData,
        initialSignerCid: event.client.cid,
        transactionData: this.transactionData,
      },
    });

    dialogRef.afterClosed().subscribe((signFields) => {
      if (signFields) {
        this.initData({
          ...this.transactionData,
          signFieldsByDocument: {
            ...this.transactionData.signFieldsByDocument,
            [event.documentData.cid]: signFields,
          },
        });
      }
      // update validity based on newly added signFields
      this.formGroup.get('clients').updateValueAndValidity();
    });
  }

  onRemoveDocument(event: IRemoveDocumentEvent) {
    const newDocumentsData = this.transactionData.documentsData.filter(
      (_, index) => index !== event.index
    );
    const oldTransactionData = this.transactionData;
    const newClientsToDocsData = oldTransactionData.formData.documents.clientsToDocs.filter(
      (_, index) => index !== event.index
    );

    this.initData({
      ...this.transactionData,
      formData: {
        ...oldTransactionData.formData,
        documents: {
          clientsToDocs: newClientsToDocsData,
        },
      },
      documentsData: newDocumentsData,
      signFieldsByDocument: oldTransactionData.signFieldsByDocument,
    });
  }

  resetForm() {
    this.initData(
      this.profile?.template || this.emptyTransactionDataFactoryService.createEmptyTransactionData()
    );
  }

  displayDevControls(): boolean {
    return isDevMode();
  }

  onDefaultSigTypeChange(type: SignatureType): void {
    this.initData({
      ...this.transactionData,
      defaultSignatureType: type,
    });
  }

  onGlobalSigTypeChange(type: SignatureType): void {
    const newSignFieldsByDocument = Object.entries(
      this.transactionData.signFieldsByDocument
    ).reduce((accSignFieldsByDocument, signFieldsByDocumentEntry) => {
      const documentId = signFieldsByDocumentEntry[0];
      const docSignFields = signFieldsByDocumentEntry[1];

      accSignFieldsByDocument[documentId] = docSignFields.map((signField) => ({
        ...signField,
        type,
      }));
      return accSignFieldsByDocument;
    }, {});

    this.initData({
      ...this.transactionData,
      signFieldsByDocument: newSignFieldsByDocument,
    });
  }

  onDocumentSwap(event: IDocumentSwapEvent): void {
    fileToBase64(event.file).subscribe((fileContent) => {
      const newDocumentData: IDocumentData = {
        cid: event.documentCid,
        name: event.file.name,
        content: fileContent,
      };
      this.initData({
        ...this.transactionData,
        documentsData: this.transactionData.documentsData.reduce(
          (newDocumentsData, documentData) => {
            if (documentData.cid === event.documentCid) {
              return newDocumentsData.concat(newDocumentData);
            }
            return newDocumentsData.concat(documentData);
          },
          []
        ),
      });
    });
  }

  getSignFieldsByClientCid(clientCid: string): IClientSignField[] {
    return Object.values(this.transactionData.signFieldsByDocument).reduce(
      (signFields, docSignFields) => {
        return signFields.concat(
          docSignFields.filter((docSignField) => docSignField.client.cid === clientCid)
        );
      },
      []
    );
  }

  onClientIdAutoIncrementChange(switchedOn: boolean): void {
    this.initData({
      ...this.transactionData,
      autoIncrementClientId: switchedOn,
    });
  }

  applyClientIdAutoIncrement(): void {
    this.clientsFormArray.controls.forEach((clientFormGroup: FormGroup, index) => {
      const personalIdControl = clientFormGroup.get('personalId');
      const personalId = (index + 1).toString().padStart(3, '0');

      if (this.transactionData.autoIncrementClientId) {
        personalIdControl.setValue(personalId, { emitEvent: false });
        personalIdControl.disable({ emitEvent: false });
      } else {
        personalIdControl.enable({ emitEvent: false });
      }
    });
  }

  onInsertTransactionData(): void {
    this.jsonEditorFormControl.setValue(this.transactionDataToTransactionDefinition(true));
  }

  onRunJson(): void {
    const transactionDefinition = this.jsonEditorFormControl.value as ITransactionDefinition;

    this.subs.sink = this.api
      .createTransaction(transactionDefinition)
      .pipe(
        mergeMap((transactionId) => {
          return this.openTransactionSaveSuccessDialog(
            transactionDefinition.startTransaction,
            transactionId
          );
        }),
        take(1)
      )
      .subscribe(
        () => {
          this.resetForm();
        },
        (error: unknown) => {
          console.error(error);
          this.openTransactionSaveErrorDialog();
        }
      );
  }
}
